<template>
  <div
    id="client-about-base"
    class="relative flex justify-center w-full py-6 bg-slate-200"
  >
    <div
      class="w-full max-w-5xl py-10 border bg-pure-white rounded-3xl border-slate-400"
      :key="id"
    >
      <div class="flex flex-col gap-1 px-10">
        <BlTextTitle4 text="About" />
        <BlTextBody2 text="Your client's personal details" />
      </div>
      <div class="px-10 py-6 client-personal-information">
        <div class="flex items-center gap-4 mb-4">
          <BlTextSubtitle1 text="Personal information" />
          <BlInputButton
            type="outlined"
            dense
            label="Edit"
            @clicked="editAboutOpened = true"
          />
        </div>
        <div class="flex flex-col gap-5">
          <!-- client name -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Name" />
            <BlTextBody2
              :text="client.name"
              class="col-span-3 text-slate-1000"
            />
          </div>
          <!-- client date of birth -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Date of birth" />
            <BlTextBody2
              :text="displayDate(client.birthday)"
              class="col-span-3 text-slate-1000"
            />
          </div>
          <!-- client status -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Status" />
            <div class="flex flex-wrap items-center col-span-3 gap-2">
              <BlChip :chip="client.status" />
            </div>
          </div>
          <!-- client date admitted -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Date admitted" />
            <BlTextBody2
              :text="displayDate(client.admitted_at)"
              class="col-span-3 text-slate-1000"
            />
          </div>
          <!-- client date of discharge -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Date of discharge" />
            <BlTextBody2
              :text="displayDate(client.archived_at)"
              class="col-span-3 text-slate-1000"
            />
          </div>
          <!-- client discharge reason -->
          <div v-if="enableSalesPipeline" class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Discharge reason" />
            <BlTextBody2
              v-if="!client.discharge_reason"
              text="-"
              class="col-span-3 text-slate-1000"
            />
            <BlTextBody2
              v-else
              :text="
                client.discharge_reason === 'other'
                  ? client.other_discharge_reason
                  : dischargeReasonOptions[client.discharge_reason]
              "
              class="col-span-3 text-slate-1000"
            />
          </div>
          <!-- client gender -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Gender" />
            <BlTextBody2
              :text="client.gender || '-'"
              class="col-span-3 capitalize text-slate-1000"
            />
          </div>
          <!-- client email -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Email" />
            <BlTextBody2
              :text="client.email || '-'"
              class="col-span-3 text-slate-1000"
            />
          </div>
          <!-- client note -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Note" />
            <BlTextBody2
              :text="client.note || '-'"
              class="col-span-3 whitespace-pre-line text-slate-1000"
            />
          </div>
          <!-- branch tags -->
          <div v-if="enableBranch" class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Branch tags" />
            <div class="flex flex-wrap items-center col-span-3 gap-2">
              <BlTextBody2 v-if="!client.branches.length" text="-" />
              <div
                v-else
                v-for="branch in client.branches"
                :key="branch.id"
                class="flex items-center justify-center px-2 truncate rounded h-7 max-w-64"
                :style="{ backgroundColor: branch.background_color || 'unset' }"
                :title="branch.name || '-'"
              >
                <BlTextBody2
                  class="truncate"
                  :text="branch.name || '-'"
                  :style="{ color: branch.color || 'unset' }"
                />
              </div>
            </div>
          </div>
          <!-- client tags -->
          <div class="grid grid-cols-4 gap-x-4">
            <BlTextBody2 class="mt-0.5" text="Client tags" />
            <div class="flex flex-wrap items-center col-span-3 gap-2">
              <BlTextBody2 v-if="!client.tags.length" text="-" />
              <div
                v-else
                v-for="tag in client.tags"
                :key="tag.id"
                class="flex items-center justify-center px-2 truncate rounded h-7 max-w-64 bg-slate-300"
                :title="tag.name || '-'"
              >
                <BlTextBody2
                  class="truncate text-slate-800"
                  :text="tag.name || '-'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="w-full h-5 bg-slate-200"></div>
      <div class="px-10 py-6 client-abc-data">
        <div class="flex items-center gap-4 mb-4">
          <BlTextSubtitle1 text="ABC data" />
          <BlInputButton
            type="outlined"
            dense
            label="Add new"
            @clicked="newAssessmentOpened = true"
          />
        </div>
        <div v-if="!assessments.length" class="grid h-64 place-items-center">
          <BlTextBody2 text="Oops, there are no ABC data available" />
        </div>
        <div v-else class="flex flex-col gap-4">
          <ClientAboutAssessment
            v-for="assessment in assessments"
            :key="assessment.id"
            :assessment="assessment"
          />
        </div>
      </div>

      <EditClientAboutModal
        v-if="editAboutOpened"
        @close-modal="editAboutOpened = false"
      />

      <NewAssessmentModal
        v-if="newAssessmentOpened"
        @close-modal="newAssessmentOpened = false"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import ClientAboutAssessment from "./about/Assessment.vue";
import EditClientAboutModal from "./about/EditClientAboutModal.vue";
import NewAssessmentModal from "./about/NewAssessmentModal.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle1 from "../BlTextSubtitle1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlIcon from "../BlIcon.vue";
import BlChip from "../BlChip.vue";
import BlFileHolder from "../BlFileHolder.vue";
import BlInputButton from "../BlInputButton.vue";
import BlBackdrop from "../BlBackdrop.vue";

export default {
  name: "ClientAbout",
  components: {
    ClientAboutAssessment,
    EditClientAboutModal,
    NewAssessmentModal,
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextBody2,
    BlIcon,
    BlChip,
    BlFileHolder,
    BlInputButton,
    BlBackdrop
  },
  props: ["id"],
  data() {
    return {
      dischargeReasonOptions: {
        graduated: "Graduated",
        family_or_personal_issues: "Family/personal issue",
        expectations_did_not_match: "Expectations didn't meet",
        schedule_conflict: "Schedule conflict",
        moving: "Moving",
        too_expensive: "Too expensive",
        other: "Others"
      },
      editAboutOpened: false,
      newAssessmentOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "client", "assessments"]),
    enableSalesPipeline() {
      return this.currentCenter.enable_sales_pipeline;
    },
    enableBranch() {
      return this.currentCenter.enable_branch;
    }
  },
  methods: {
    ...mapActions([""]),
    displayDate(date, format = "DD/MM/YYYY") {
      if (!date) return "-";
      return moment(date).format(format);
    }
  }
};
</script>

<style scoped lang="scss">
.bl-client-about {
  .bl-client-about-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/ClientAbout.vue